import React, { ChangeEvent, useMemo, useState } from "react";

import { useGetDatasetMappersData } from "api/api-hooks/dataset-mapper-hooks";
import { useUpdateDatasetInfo } from "api/api-hooks/datasets-hooks";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import { DatasetStringValueMapperDTO } from "components/dashboard/charts/interfaces";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { BoundType, DatasetAccessType } from "utils/enums";
import {
  DATASET_ACCESS_TYPES,
  DATASET_DATA_TYPES,
  DATASET_DATA_TYPES_FOR_CONTINUOUS,
  normalizeDataTypeForAccessType,
} from "../../utils/utils";

interface IEditDatasetProps {
  selectedDatasets: DatasetInfo[];
  closeDialog: () => void;
  setSelectedDatasets: (arr: DatasetInfo[]) => void;
}

const emptyMapper: DatasetStringValueMapperDTO = {
  id: undefined,
  title: T("word.none"),
  global: false,
  mappers: [],
};

const boundToSelectItems = [
  { label: T("word.device-id"), value: BoundType.MDM_DEVICE_ID },
  { label: T("word.device-identifier"), value: BoundType.MDM_DEVICE_IDENTIFIER },
  { label: T("word.object-id"), value: BoundType.MDM_OBJECT_ID },
  { label: T("word.object-identifier"), value: BoundType.MDM_OBJECT_IDENTIFIER },
  { label: T("word.unbound-none"), value: null },
];

const EditDatasetDialog = ({ selectedDatasets, closeDialog, setSelectedDatasets }: IEditDatasetProps) => {
  const [datasetsArr, setDatasetsArr] = useState<DatasetInfo[]>(selectedDatasets);

  const titleBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputText
        className="w-full"
        value={options.title || ""}
        name="title"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const dataTypeBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-full"
        options={
          options.accessType === DatasetAccessType.CONTINUOUS
            ? DATASET_DATA_TYPES_FOR_CONTINUOUS
            : DATASET_DATA_TYPES
        }
        value={options.dataType || ""}
        name="dataType"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const accessTypeBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-full"
        options={DATASET_ACCESS_TYPES}
        value={options.accessType || ""}
        name="accessType"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const mapperBodyTemplate = (options: DatasetInfo) => {
    return <MapperDropdown options={options} inputHandler={inputChangeHandler} />;
  };

  const unitBodyTemplate = (options: DatasetInfo) => {
    return (
      <DatasetUnitSelector
        className="w-8rem responsive-full-width"
        value={options.unit || ""}
        name="unit"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const decimalPlacesBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputNumber
        value={options.decimalPlaces ?? null}
        onValueChange={e => inputChangeHandler(options, e)}
        className="responsive-full-width"
        name="decimalPlaces"
        min={0}
        max={10}
        inputStyle={{ width: "100%" }}
      />
    );
  };

  const deviceIdBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputNumber
        value={options.deviceId || 0}
        className="responsive-full-width"
        name="deviceId"
        onValueChange={e => inputChangeHandler(options, e)}
        inputStyle={{ width: "5rem" }}
      />
    );
  };

  const boundToIdBodyTemplate = (options: DatasetInfo) => {
    return (
      <InputText
        className="w-10rem responsive-full-width"
        value={options.boundToId || ""}
        name="boundToId"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const boundToTypeBodyTemplate = (options: DatasetInfo) => {
    return (
      <Dropdown
        className="w-14rem responsive-full-width"
        value={options?.boundToType}
        options={boundToSelectItems}
        name="boundToType"
        onChange={e => inputChangeHandler(options, e)}
      />
    );
  };

  const inputChangeHandler = (
    options: DatasetInfo,
    e: InputNumberValueChangeEvent | DropdownChangeEvent | ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;

    setDatasetsArr(prev =>
      prev.map(dataset => {
        if (dataset.identifier !== options.identifier) return dataset;

        const updated = { ...dataset, [name]: value };

        if (name === "accessType") {
          updated.dataType = normalizeDataTypeForAccessType(value as DatasetAccessType, updated.dataType);
        }

        if (name === "dataType") {
          updated.dataType = normalizeDataTypeForAccessType(
            updated.accessType as DatasetAccessType,
            value as string
          );
        }

        return updated;
      })
    );
  };

  const { mutateAsync } = useUpdateDatasetInfo(true);

  const saveChangesHandler = async () => {
    try {
      if (datasetsArr.length === 0) {
        easasAlert({
          summary: T("alert.no-changes-detected"),
          detail: T("alert.please-make-changes-before-saving"),
          severity: "warn",
        });
        return;
      }

      const results = await Promise.allSettled(
        datasetsArr.map(async dataset => {
          return mutateAsync(dataset);
        })
      );

      const successFullMutations = results.filter(result => result.status === "fulfilled");
      const failedMutations = results.filter(result => result.status === "rejected");

      if (successFullMutations.length === results.length) {
        easasAlert({
          summary: T("alert.success"),
          detail: T("alert.all-changes-were-saved-successfully"),
          severity: "success",
        });
      } else {
        easasAlert({
          summary: T("alert.error"),
          detail: T("error.datasets-failed-to-update", { count: failedMutations.length }),
          severity: "error",
        });
      }
    } catch (e) {
      console.warn("Error updating datasets", e);
      easasAlert({
        summary: T("alert.error"),
        detail: T("error.an-error-occurred-while-updating-datasets"),
        severity: "error",
      });
    }

    closeDialog();
    setSelectedDatasets([]);
  };

  return (
    <div className="flex flex-column gap-3">
      <DataTable
        value={datasetsArr}
        className="p-datatable-gridlines mb-5"
        style={{ width: "100%" }}
        breakpoint="910px"
      >
        <Column
          field="title"
          header={T("word.title")}
          className="w-full p-0 mobile-flex-column"
          body={titleBodyTemplate}
        />
        <Column
          field="dataType"
          header={T("word.type")}
          className="w-1 p-0 mobile-flex-column"
          body={dataTypeBodyTemplate}
        />
        <Column
          field="accessType"
          header={T("label.access-type")}
          className="w-1 p-0 mobile-flex-column"
          body={accessTypeBodyTemplate}
        />
        <Column
          field="mapper"
          header={T("word.mapper")}
          className="w-1 p-0 mobile-flex-column"
          body={mapperBodyTemplate}
        />
        <Column
          field="unit"
          header={T("word.unit")}
          className="w-1 p-0 mobile-flex-column"
          body={unitBodyTemplate}
        />
        <Column
          field="decimalPlaces"
          header={T("config.decimal-places")}
          style={{ width: "4rem" }}
          className="p-0 mobile-flex-column"
          body={decimalPlacesBodyTemplate}
        />
        <Column
          field="deviceId"
          header={T("word.device-id")}
          className="p-0 mobile-flex-column"
          body={deviceIdBodyTemplate}
        />
        <Column
          field="boundToId"
          header={T("word.bound-to-id")}
          className="p-0 mobile-flex-column"
          body={boundToIdBodyTemplate}
        />
        <Column
          field="boundToType"
          header={T("word.bound-to-type")}
          className="w-1 p-0 mobile-flex-column"
          body={boundToTypeBodyTemplate}
        />
      </DataTable>

      <div className="buttons flex justify-content-end gap-2 mt-2">
        <Button
          label={T("word.cancel")}
          icon="pi pi-times"
          onClick={closeDialog}
          className="component-editor-button icon-red"
        />
        <Button
          label={T("action.save")}
          icon="pi pi-check"
          onClick={saveChangesHandler}
          className="component-editor-button icon-green"
        />
      </div>
    </div>
  );
};

export default EditDatasetDialog;

const MapperDropdown: React.FC<{
  options: DatasetInfo;
  inputHandler: (options: DatasetInfo, e: any) => void;
}> = ({ options, inputHandler }) => {
  const { data: currentMappers } = useGetDatasetMappersData();
  const mappers = useMemo(() => [emptyMapper, ...(currentMappers ?? [])], [currentMappers]);
  return (
    <Dropdown
      className="w-full"
      value={options?.stringValueMapper?.toString() || ""}
      options={mappers}
      optionValue="id"
      optionLabel="title"
      name="stringValueMapper"
      onChange={e => inputHandler(options, e)}
    />
  );
};
